/**
 * Fast Track onboarding helpers. The pending age declaration is kept on the
 * device between the quick check and account creation so it survives social
 * sign-in redirects and email confirmation on the same phone.
 */
export const AGE_DECLARATION_VERSION = "age-decl-2026-10";
export const TERMS_VERSION = "terms-2026-10";
const KEY = "vitolier:fast_track_declaration";
/** A declaration older than this must be made again. */
export const DECLARATION_TTL_MS = 24 * 60 * 60 * 1000;
export const CONFIRMATION_TTL_MS = 30 * DECLARATION_TTL_MS;

export interface PendingDeclaration {
  country: string;
  minAge: number;
  declaredAt: string;
}

export function savePendingDeclaration(d: PendingDeclaration): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(d));
  } catch {
    /* storage unavailable */
  }
}

export function readPendingDeclaration(now: number = Date.now()): PendingDeclaration | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as PendingDeclaration;
    if (!d?.country || typeof d.minAge !== "number" || !d.declaredAt) return null;
    const t = Date.parse(d.declaredAt);
    if (Number.isNaN(t) || now - t > DECLARATION_TTL_MS) return null;
    return d;
  } catch {
    return null;
  }
}

export function clearPendingDeclaration(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** True when the signed-in account already carries an age declaration or a date of birth. */
export function hasAccountDeclaration(user: { user_metadata?: Record<string, unknown> } | null): boolean {
  const m = user?.user_metadata ?? {};
  return !!(m.age_declared_at || m.date_of_birth);
}

/**
 * Email awaiting confirmation on this device. Lets a reload or a return from
 * the Mail app reopen "Check your email" instead of asking for the email and
 * password again. Only the address is stored — never the password.
 */
const AWAIT_KEY = "vitolier:fast_track_awaiting";

export function saveAwaitingConfirmation(email: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(AWAIT_KEY, JSON.stringify({ email, at: now }));
  } catch {
    /* storage unavailable */
  }
}

export function readAwaitingConfirmation(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(AWAIT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as { email?: string; at?: number };
    if (!d?.email || typeof d.at !== "number" || now - d.at > CONFIRMATION_TTL_MS) return null;
    return d.email;
  } catch {
    return null;
  }
}

export function clearAwaitingConfirmation(): void {
  try {
    localStorage.removeItem(AWAIT_KEY);
  } catch {
    /* ignore */
  }
}

/** Reads what an email confirmation link put in the URL hash. */
export function readAuthReturn(hash: string): { inProgress: boolean; error: string | null } {
  const p = new URLSearchParams(hash.replace(/^#/, ""));
  const error = p.get("error_description") || p.get("error");
  return { inProgress: !error && p.has("access_token"), error };
}

export type FastTrackScreen = "check" | "account" | "check-email" | "existing" | "finishing";

export function initialFastTrackScreen(hasDeclaration: boolean, awaiting: string | null, authReturn: { inProgress: boolean; error: string | null }): FastTrackScreen {
  if (authReturn.inProgress) return "finishing";
  if (authReturn.error || awaiting) return "check-email";
  return hasDeclaration ? "account" : "check";
}

const DRAFT_KEY = "vitolier:fast_track_account_draft";
export function saveFastTrackAccountDraft(email: string, acceptedTerms: boolean): void {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ email, acceptedTerms, at: Date.now() })); } catch { /* optional */ }
}
export function readFastTrackAccountDraft(): { email: string; acceptedTerms: boolean } | null {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? "null");
    if (!d || typeof d.at !== "number" || Date.now() - d.at > DECLARATION_TTL_MS || typeof d.email !== "string") return null;
    return { email: d.email, acceptedTerms: d.acceptedTerms === true };
  } catch { return null; }
}
export function clearFastTrackAccountDraft(): void {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* optional */ }
}
